import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'

import * as _ from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { SessionCountByProgram } from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'
import LoadingIndicator from '@lyrahealth-inc/ui-core/lib/atoms/loadingIndicator/LoadingIndicator'
import BootstrapContainer from '@lyrahealth-inc/ui-core/lib/molecules/layouts/bootstrapContainer/BootstrapContainer'
import DataTable from '@lyrahealth-inc/ui-core/lib/molecules/table/DataTable'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { LightbulbIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LightbulbIcon'
import { AwaitingResultsIllustration } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/illustrations/AwaitingResultsIllustration'
import { InlineInfoBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineInfoBanner/InlineInfoBanner'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'

import styles from './clientsDashboard.module.scss'
import { getDashboardTableColumns } from './DashboardTableCols'
import AvailabilityCard from '../../common/components/availabilityCard/AvailabilityCard'
import { ROLES } from '../../common/constants/appConstants'
import { GET_CLIENTS } from '../../common/constants/reduxConstants'
import { CLIENTS_DETAILS, OUTCOMES_CONSENT } from '../../common/constants/routingConstants'
import { hasErrorForAction, hasRole } from '../../common/utils/utils'
import { getAlertsState } from '../../data/alertsSelectors'
import { Alert } from '../../data/alertTypes'
import { getAuthRoles, getAuthUserId, getAuthUserOutcomesAgreed } from '../../data/auth/authSelectors'
import { getRequestPaymentCustomers } from '../../data/requestPayment/requestPaymentSelectors'
import { RootState, useAppDispatch } from '../../data/store'
import { getProviderCapacity } from '../../providers/data/providersDataActions'
import { getProvider } from '../../providers/individualProvider/data/providerDetailsActions'
import { getClients } from '../data/clientsDataActions'
import { getClientsDataClientList } from '../data/clientsDataSelectors'
import { clientSelected } from '../individualClient/data/clientDetailsAutoActions'

interface DashboardClientObject extends ClientObject {
  display_visits_per_issue_per_year?: number | undefined
  display_visits_per_issue_per_year_by_program?: SessionCountByProgram | undefined
}

type ClientsDashboardProps = {
  // When provided (transition experience only), each eligible row renders an "Add to Engage" action
  onAddToEngage?: (client: ClientObject) => void
  eligibleEmployerValues?: ReadonlySet<string>
}

const ClientsDashboard: React.FC<ClientsDashboardProps> = ({ onAddToEngage, eligibleEmployerValues }) => {
  const dispatch = useAppDispatch()
  const customers = useSelector(getRequestPaymentCustomers)
  const outcomesAgreed = useSelector(getAuthUserOutcomesAgreed)
  const userId = useSelector(getAuthUserId)
  const userRoles: string[] = useSelector(getAuthRoles)
  const alerts: Alert[] = useSelector(getAlertsState)
  const rawClients = useSelector(getClientsDataClientList)
  const clients = rawClients ?? []
  const navigate = useNavigate()
  const { isDAProviderMigrated, isLegacyClientTransitionEnabled } = useFlags()
  const { colors } = useTheme()
  const showTransitionUpdates = isDAProviderMigrated && isLegacyClientTransitionEnabled
  const hasClientsLoadError = hasErrorForAction(alerts, GET_CLIENTS)
  // The error alert can be dismissed, expired, or replaced while clients never loaded. Quarantines failures
  // so the loading card can't come back.
  const [clientsLoadFailed, setClientsLoadFailed] = useState(false)
  useEffect(() => {
    if (hasClientsLoadError) {
      setClientsLoadFailed(true)
    }
  }, [hasClientsLoadError])
  // rawClients is undefined until GET_CLIENTS resolves; [] is a genuinely empty response
  const isLoadingClients = !customers || (rawClients === undefined && !hasClientsLoadError && !clientsLoadFailed)

  useEffect(() => {
    if (outcomesAgreed && userId) {
      Promise.resolve(dispatch(getClients(userId))).catch(() => {})
    }
    if (userId) {
      Promise.resolve(dispatch(getProvider(userId))).catch(() => {})
      // @ts-expect-error auto-thunk action - middleware handles the dispatch
      dispatch(getProviderCapacity({ id: userId }))
    }
  }, [dispatch, outcomesAgreed, userId])

  const renderClientList = () => {
    // create the list of clients to be shown in the DataTable
    let filteredClient = clients
    //add display_visits_per_issue_per_year and display_visits_per_issue_per_year_by_program for each client if filteredClient array exists
    if (Array.isArray(filteredClient)) {
      filteredClient = filteredClient.map((item: DashboardClientObject) => {
        const matchingCustomer = customers?.find((customer) => customer.value === item.employer)
        return {
          ...item,
          display_visits_per_issue_per_year: matchingCustomer?.display_visits_per_issue_per_year,
          display_visits_per_issue_per_year_by_program: matchingCustomer?.display_visits_per_issue_per_year_by_program,
        }
      })
    }

    if (_.isEmpty(filteredClient)) {
      if (showTransitionUpdates) {
        return (
          <div className={styles['clients-empty-container']} data-test-id='ClientsDashboard-transitionEmpty'>
            <p>
              <FormattedMessage
                defaultMessage='No clients to display'
                description='Message shown when the legacy client list is empty'
              />
            </p>
          </div>
        )
      }
      return (
        <div className={styles['awaiting-results-container']}>
          <AwaitingResultsIllustration strokeColor={styles.x_primary_action} />
          <h2>Awaiting Client Results</h2>
          <p>
            Once client outcomes are submitted,
            <br />
            you can see the results right here.
          </p>
          <a
            href='https://provider-support.lyrahealth.com/hc/en-us/articles/115012608808'
            target='_blank'
            rel='noreferrer'
          >
            Learn more
          </a>
        </div>
      )
    }
    return (
      <div className={styles['clients-dashboard-container']}>
        <DataTable
          data={filteredClient as unknown as Dict[]}
          className={
            showTransitionUpdates
              ? `${styles['clients-table']} ${styles['clients-table-transition']}`
              : styles['clients-table']
          }
          striped={false}
          highlight={false}
          resizable={false}
          defaultSorted={[
            {
              id: 'date_submitted',
              desc: true,
            },
          ]}
          destinationSelector='id'
          // @ts-expect-error TS(2322): Type '(state: RootState, rowInfo: any) => void' is not a... Remove this comment to see the full error message
          rowClickHandler={rowClickHandler}
          // @ts-expect-error TS(2322): Type '({ Header: string; accessor: string; Cell: (... Remove this comment to see the full error message
          columns={getDashboardTableColumns({ onAddToEngage, eligibleEmployerValues })}
        />
      </div>
    )
  }

  const rowClickHandler = (_state: RootState, rowInfo: any) => {
    dispatch(clientSelected(rowInfo.original))
    navigate(CLIENTS_DETAILS.route)
  }

  if (hasRole(userRoles, ROLES.ICAS_PROVIDER)) {
    return (
      <BootstrapContainer col='col-md-10 col-md-offset-1' style={{ overflow: 'visible' }}>
        <AvailabilityCard />
      </BootstrapContainer>
    )
  }
  if (!outcomesAgreed) {
    return <Navigate to={OUTCOMES_CONSENT.route} />
  }
  if (!showTransitionUpdates && (!customers || (!clients && !hasErrorForAction(alerts, GET_CLIENTS)))) {
    return (
      <div style={{ marginTop: '50px', textAlign: 'center' }}>
        <LoadingIndicator color={styles.x_success} size={45} />
      </div>
    )
  }
  return (
    <BootstrapContainer col='col-md-10 col-md-offset-1' style={{ overflow: 'visible' }}>
      {!isDAProviderMigrated && <AvailabilityCard />}
      <div className={styles['title-block']}>
        {!isDAProviderMigrated && <h1 data-test-id='ClientsDashboard-client-list'>Clients</h1>}
        {showTransitionUpdates && (
          <InlineInfoBanner
            testId='ClientsDashboard-transitionBanner'
            textSize={BodyTextSize.SMALL}
            icon={<LightbulbIcon size={24} fillColor={colors.components.inlineInfoBanner.infoIcon.fill} />}
            title={
              <FormattedMessage
                defaultMessage='Transition eligible clients to the Engage platform'
                description='Title of the banner prompting providers to add their legacy clients to the Engage platform'
              />
            }
            text={
              <FormattedMessage
                defaultMessage='Adult clients can now be added to your Engage caseload for streamlined scheduling, billing, video sessions and more. Intake <underline>must</underline> be completed and invoiced via the Legacy Payment Form before moving a client to Engage.'
                description='Body of the banner prompting providers to add their legacy clients to the Engage platform'
                values={{
                  underline: (chunks: React.ReactNode[]) => (
                    <BodyText
                      size={BodyTextSize.SMALL}
                      color={colors.components.inlineInfoBanner.text}
                      underline
                      text={chunks}
                    />
                  ),
                }}
              />
            }
          />
        )}
        {showTransitionUpdates ? (
          <p data-test-id='ClientsDashboard-transitionDescription'>
            <FormattedMessage
              defaultMessage='This tab lists clients seen prior to your transition to Engage who have had outcomes or billing activity in the last 60 days (inactive clients reappear when activity resumes). Until a client is added to Engage, you will continue documenting and scheduling follow-up sessions off-platform, and billing via the Legacy Payment Form. Outcomes go automatically to adult clients monthly (excludes minors and family therapy clients). Details in the <helpCenterLink>Help Center</helpCenterLink>.'
              description='Description of the legacy client list shown to providers who can transition clients to the Engage platform'
              values={{
                helpCenterLink: (chunks) => (
                  <a
                    href='https://provider-support.lyrahealth.com/hc/en-us/articles/54314011092755-Working-with-Legacy-Clients-on-Engage'
                    target='_blank'
                    rel='noreferrer'
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </p>
        ) : (
          <p>
            Your client list displays clients with Outcomes <b>or</b> billing activity within the last 60 days. You can
            resend Outcomes to eligible clients* within their record below. Clients without activity in the last 60 days
            will reappear if there is new activity. <br></br>
            <br></br>
            <i>
              *Outcomes are automatically sent to all adult clients on a monthly basis (minors and clients participating
              in family therapy are not eligible to receive Outcomes). For more information on our Automated Outcomes
              process, please visit our{' '}
              <a
                href='https://provider-support.lyrahealth.com/hc/en-us/articles/115012608808-Automated-Outcomes-Collection-Process-and-FAQ'
                target='_blank'
                rel='noreferrer'
              >
                Help Center
              </a>
              .
            </i>
          </p>
        )}
      </div>
      {showTransitionUpdates && isLoadingClients ? (
        <div className={styles['clients-loading-container']} data-test-id='ClientsDashboard-transitionLoading'>
          <LoadingIndicator color={colors.iconActive} size={80} />
          <p>
            <FormattedMessage
              defaultMessage='Loading legacy clients'
              description='Message shown while the legacy client list is loading'
            />
          </p>
        </div>
      ) : (
        renderClientList()
      )}
    </BootstrapContainer>
  )
}

export default ClientsDashboard
